import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook that manages CloudFront signed cookie refresh for HLS streaming.
 *
 * Call `requestAccess(assetPath)` before loading an HLS manifest.
 * The hook will periodically refresh the cookies before they expire.
 *
 * Usage:
 * ```ts
 * const { requestAccess, revokeAccess } = useMediaAccess();
 *
 * // Before playing
 * await requestAccess('media/hls/speaker-123/video');
 *
 * // When done
 * revokeAccess('media/hls/speaker-123/video');
 * ```
 */
export function useMediaAccess() {
    const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

    const requestAccess = useCallback(async (assetPath: string): Promise<boolean> => {
        try {
            const res = await fetch(`/api/media-access?asset=${encodeURIComponent(assetPath)}`, {
                credentials: 'include'
            });

            if (!res.ok) {
                console.error(`[useMediaAccess] Failed to get access for ${assetPath}:`, res.status);
                return false;
            }

            const data = await res.json();

            // Set up periodic cookie refresh (refresh at 80% of TTL)
            const refreshMs = (data.expiresInSeconds ?? 300) * 0.8 * 1000;

            // Clear any existing interval for this asset
            const existing = intervalsRef.current.get(assetPath);
            if (existing) {
                clearInterval(existing);
            }

            const interval = setInterval(async () => {
                try {
                    const refreshRes = await fetch(
                        `/api/media-access?asset=${encodeURIComponent(assetPath)}`,
                        { credentials: 'include' }
                    );
                    if (!refreshRes.ok) {
                        console.warn(`[useMediaAccess] Cookie refresh failed for ${assetPath}`);
                    }
                } catch (err) {
                    console.warn(`[useMediaAccess] Cookie refresh error for ${assetPath}:`, err);
                }
            }, refreshMs);

            intervalsRef.current.set(assetPath, interval);
            return true;
        } catch (err) {
            console.error(`[useMediaAccess] Error requesting access for ${assetPath}:`, err);
            return false;
        }
    }, []);

    const revokeAccess = useCallback((assetPath: string) => {
        const interval = intervalsRef.current.get(assetPath);
        if (interval) {
            clearInterval(interval);
            intervalsRef.current.delete(assetPath);
        }
    }, []);

    // Clean up all intervals on unmount
    useEffect(() => {
        return () => {
            for (const interval of intervalsRef.current.values()) {
                clearInterval(interval);
            }
            intervalsRef.current.clear();
        };
    }, []);

    return { requestAccess, revokeAccess };
}
